//src/components/DeleteRestoreFile

import React, { useContext, useState } from "react";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import Snackbar from "@mui/material/Snackbar";
import MuiAlert from "@mui/material/Alert";
import secureLocalStorage from "react-secure-storage";
import fileContext from "../../context/fileContext";
import { useParams } from "react-router-dom";
import { api } from "../../utils/axios-instance";
import { useDarkMode } from "../../context/darkModeContext";

// images from public folder
const Danger = `${process.env.PUBLIC_URL}/assets/danger.webp`;

const DeleteRestoreFile = ({ fileName, owner, id, remove }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [loadingDelete, setLoadingDelete] = useState(false);
  const [loadingRestore, setLoadingRestore] = useState(false);

  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarSeverity, setSnackbarSeverity] = useState("success");
  const [snackbarMessage, setSnackbarMessage] = useState("");

  const { deptName } = useParams();
  const context = useContext(fileContext);
  const {
    removeFile,
    setAnchorEl,
    updateDepartmentFiles,
    setRefreshRecycleBin,
  } = context;
  const { darkMode } = useDarkMode();

  const openDialog = () => setIsOpen(true);

  const closeDialog = () => {
    setIsOpen(false);
    setAnchorEl(null);
  };

  const showToast = (severity, message) => {
    setSnackbarSeverity(severity);
    setSnackbarMessage(message);
    setSnackbarOpen(true);
  };

  const handleSnackbarClose = (_, reason) => {
    if (reason === "clickaway") return;
    setSnackbarOpen(false);
  };

  const restoreFile = async () => {
    setLoadingRestore(true);
    try {
      const res = await api.delete(`/file/restore-file/${id}/`);

      showToast("success", res?.data?.message || "File restored successfully.");

      // remove from UI immediately (since it left recycle bin)
      removeFile?.(id);
      remove?.(id);

      setRefreshRecycleBin(true);

      if (deptName) updateDepartmentFiles?.(deptName);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        "Error restoring file.";
      showToast("error", msg);
      setRefreshRecycleBin(true);
    } finally {
      setLoadingRestore(false);
    }
  };

  const handlePermanentDelete = async () => {
    setLoadingDelete(true);

    const profileData = JSON.parse(secureLocalStorage.getItem("profileData"));

    // Permission check (UI-side; backend enforces authoritatively).
    if (
      !(profileData?.id === owner || profileData?.role_priv === "org_admin")
    ) {
      showToast("error", "You are not allowed to delete this file.");
      setLoadingDelete(false);
      setRefreshRecycleBin(true);
      setTimeout(() => closeDialog(), 800);
      return;
    }

    try {
      // Backend owns the destructive work: it deletes the Firestore doc,
      // the canonical GCS object, and (once updated) the version snapshot
      // blobs. The frontend no longer touches storage directly -- that path
      // was silently broken by an org_id typo and duplicated backend logic.
      const res = await api.delete(`/file/delete-file/${id}/`);

      showToast("success", res?.data?.detail || "File deleted permanently.");

      removeFile?.(id);
      remove?.(id);

      if (deptName) updateDepartmentFiles?.(deptName);
      setRefreshRecycleBin(true);

      setTimeout(() => closeDialog(), 700);
    } catch (err) {
      const msg =
        err?.response?.data?.error ||
        err?.response?.data?.detail ||
        err?.message ||
        "Error deleting the file.";
      showToast("error", msg);
      setRefreshRecycleBin(true);
    } finally {
      setLoadingDelete(false);
    }
  };

  return (
    <>
      <div className="flex flex-col space-y-1 text-left">
        <button
          onClick={openDialog}
          className="text-[#D1293D] hover:bg-red-200 hover:text-red-600 duration-100 px-2 py-1 border rounded-md"
        >
          Delete File
        </button>

        <button
          onClick={restoreFile}
          disabled={loadingRestore}
          className="text-[#167e27] hover:bg-green-200 hover:text-green-600 duration-100 px-2 py-1 border rounded-md disabled:opacity-60"
        >
          {loadingRestore ? "Restoring..." : "Restore File"}
        </button>
      </div>

      <Dialog
        open={isOpen}
        onClose={closeDialog}
        PaperProps={{
          style: {
            borderRadius: "5px",
            backgroundColor: darkMode ? "#1f2937" : "#ffffff",
          },
        }}
      >
        <DialogContent
          style={{ backgroundColor: darkMode ? "#1f2937" : "#FEF2F2" }}
        >
          <div className="my-2 p-3 flex flex-col justify-center items-center gap-6">
            <img src={Danger} alt="." />
            <h2
              className={`text-2xl font-semibold ${darkMode ? "text-gray-200" : "text-gray-700"}`}
            >
              Are you sure?
            </h2>

            <div className={`text-center ${darkMode ? "text-gray-300" : ""}`}>
              <p>
                This action will delete the{" "}
                <strong className="hover:underline">
                  {(fileName || "").split("_TS=")[0]}
                </strong>{" "}
                file.
              </p>
              <p>You won't be able to revert this action!</p>
            </div>

            <div className="flex flex-row justify-center items-center gap-2">
              <button
                className="px-4 py-1 rounded-lg shadow-sm bg-red-500 text-white disabled:opacity-60 hover:bg-red-600"
                onClick={handlePermanentDelete}
                disabled={loadingDelete}
              >
                {loadingDelete ? "Deleting..." : "Confirm"}
              </button>

              <button
                className={`px-4 py-1 mx-2 rounded-lg shadow-sm border ${darkMode ? "border-gray-600 text-gray-300 hover:bg-gray-700" : "border-gray-300 hover:bg-gray-50"}`}
                onClick={closeDialog}
              >
                Cancel
              </button>
            </div>

            <Snackbar
              open={snackbarOpen}
              autoHideDuration={6000}
              onClose={handleSnackbarClose}
            >
              <MuiAlert
                elevation={6}
                variant="filled"
                onClose={handleSnackbarClose}
                severity={snackbarSeverity}
              >
                {snackbarMessage}
              </MuiAlert>
            </Snackbar>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default DeleteRestoreFile;